import type { FC } from 'react';

import { useCms, useContent } from '@eneco/cms';
import { Head } from '@eneco/next';

import { pageMetaData } from './pageMeta';

interface MetaTag {
  tag?: string;
  attributes?: Record<string, string>;
}

/**
 * A tag's key by what it is (`og:title`, `description`, `canonical`), as `CmsRouteContainer`'s `PageHead`
 * keys its own: Next's `Head` keeps one tag per key, so the page head and Sitecore's tags do not both
 * end up in it. Sitecore's are rendered later and win, as on the legacy lane.
 */
const tagKey = ({ tag, attributes = {} }: MetaTag): string =>
  attributes.property ?? attributes.name ?? (attributes.rel === 'canonical' ? 'canonical' : `${tag}-${JSON.stringify(attributes)}`);

/**
 * `MetaData` block for the CMS-agnostic lane, serving **both** providers.
 *
 * Mirrors `libs/sitecore/components/system/src/MetaData/MetaData.tsx`: the tags Sitecore's
 * `MetaDataContentResolver` computes on its server, rendered into the head as it hands them over. Its
 * fields are a list, which the Sitecore adapter hands over as an object keyed by position (`"0"`, `"1"`, …).
 *
 * The Sanity block has no fields (ADR-0104): there the tags come from the page's own fields
 * ({@link SanityMetaData}). Either way they appear only where the page carries this block, as on
 * the legacy lane, where an Over ons page has none of them.
 */
const MetaData: FC = () => {
  const { page } = useCms();
  const content = useContent<Record<string, MetaTag | null>>();

  if (page.context.cmsSource === 'sanity') return <SanityMetaData />;

  const tags = Object.values(content);
  if (tags.length === 0) return null;

  return (
    <Head>
      {tags.map(property => {
        if (!property?.tag) return null;

        const TagName = property.tag;
        return <TagName key={tagKey(property)} {...(property?.attributes ?? {})} />;
      })}
    </Head>
  );
};

/** What Sitecore's resolver computes, from a Sanity page's own fields (`pageMetaData`), keyed as `tagKey` keys them. */
const SanityMetaData: FC = () => {
  const { page, adapter } = useCms();
  const meta = pageMetaData(page, adapter);

  return (
    <Head>
      {meta.openGraph.title && <meta key="og:title" property="og:title" content={meta.openGraph.title} />}
      {meta.openGraph.description && (
        <meta key="og:description" property="og:description" content={meta.openGraph.description} />
      )}
      {meta.openGraph.image && <meta key="og:image" property="og:image" content={meta.openGraph.image} />}
      {meta.canonical && <link key="canonical" rel="canonical" href={meta.canonical} />}
      {meta.alternates.map(({ hrefLang, href }) => (
        <link key={`alternate:${hrefLang}`} rel="alternate" href={href} hrefLang={hrefLang} />
      ))}
    </Head>
  );
};

export default MetaData;
